import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  Shield, CheckCircle2, Clock, AlertCircle, Loader2, ExternalLink, Fingerprint
} from "lucide-react";

export default function IdentityVerificationCard({ onVerificationStart, compact = false }) {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const token = localStorage.getItem("token");

  const isVerified = user?.identity_verified;
  const status = user?.identity_verification_status || "none";
  const verifiedAt = user?.identity_verified_at;

  useEffect(() => {
    // Fetch fresh verification status
    const fetchStatus = async () => {
      if (!user?.id) return;
      try {
        const res = await axios.get(
          `${API}/professionals/${user.id}/verification-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVerificationStatus(res.data);
      } catch (error) {
        // Ignore errors
      }
    };
    fetchStatus();
  }, [user?.id, token]);

  const startVerification = async () => {
    setLoading(true);
    try {
      // Call backend to create Persona inquiry
      const res = await axios.post(
        `${API}/professionals/verify-identity`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { inquiry_id, session_token, status: inquiryStatus } = res.data;
      
      // Try to load Persona SDK
      try {
        const Persona = (await import("persona")).default;
        
        const client = new Persona.Client({
          inquiryId: inquiry_id,
          ...(session_token ? { sessionToken: session_token } : {}),
          templateId: process.env.REACT_APP_PERSONA_TEMPLATE_ID || "itmpl_placeholder",
          environment: "sandbox",
          onReady: () => {
            client.open();
          },
          onComplete: async ({ inquiryId, status }) => {
            toast.success("Verification submitted! We'll review it shortly.");
            setLoading(false);
            // Refresh user data
            if (refreshUser) refreshUser();
            if (onVerificationStart) onVerificationStart();
          },
          onCancel: ({ inquiryId }) => {
            toast.info("Verification cancelled. You can resume anytime.");
            setLoading(false);
          },
          onError: (error) => {
            console.error("Persona error:", error);
            toast.error("Verification error. Please try again.");
            setLoading(false);
          }
        });
      } catch (sdkError) {
        // Persona SDK not available or sandbox mode
        // Use sandbox approval endpoint for testing
        toast.info("Verification started in sandbox mode. Click 'Simulate Approval' to test.");
        setLoading(false);
        if (onVerificationStart) onVerificationStart();
      }
      
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to start verification";
      if (detail.includes("already verified")) {
        toast.success("Your identity is already verified!");
      } else {
        toast.error(detail);
      }
      setLoading(false);
    }
  };

  const sandboxApprove = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/professionals/verify-identity/sandbox-approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Identity verified successfully!");
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setLoading(false);
    }
  };

  // Compact badge for verified users
  if (compact && isVerified) {
    return (
      <Badge className="bg-green-100 text-green-700 flex items-center gap-1" data-testid="identity-verified-badge">
        <Shield className="w-3 h-3" />
        Verified
      </Badge>
    );
  }

  // Already verified - show success state
  if (isVerified) {
    return (
      <Card className="border-green-200 bg-green-50" data-testid="identity-verified-card">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-green-900 font-semibold flex items-center gap-2">
                Identity Verified
                <Badge className="bg-green-600 text-white text-xs">
                  <Shield className="w-3 h-3 mr-1" />
                  KYC Complete
                </Badge>
              </p>
              <p className="text-green-700 text-sm">
                Verified on {verifiedAt ? new Date(verifiedAt).toLocaleDateString() : "recently"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Verification pending
  if (status === "pending" || status === "created") {
    return (
      <Card className="border-yellow-200 bg-yellow-50" data-testid="identity-pending-card">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-yellow-900 font-medium">Verification In Progress</p>
                <p className="text-yellow-700 text-sm">Your identity is being verified. This usually takes a few minutes.</p>
              </div>
            </div>
            
            {/* Sandbox testing buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={startVerification}
                disabled={loading}
                className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Resume Verification
              </Button>
              <Button
                size="sm"
                onClick={sandboxApprove}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="sandbox-approve-btn"
              >
                Simulate Approval (Sandbox)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Declined or failed
  if (status === "declined" || status === "failed") {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="identity-failed-card">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-red-900 font-medium">Verification {status === "declined" ? "Declined" : "Failed"}</p>
                <p className="text-red-700 text-sm">Please try again with valid identification documents.</p>
              </div>
            </div>
            <Button
              onClick={startVerification}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white w-fit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not started - show CTA card
  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="identity-verify-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Fingerprint className="w-5 h-5 text-blue-600" />
          Verify Your Identity
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-600 text-sm">
          Get verified and unlock <span className="font-semibold text-blue-700">priority access to more jobs!</span> Customers prefer hiring verified professionals.
        </p>
        
        <div className="bg-white/60 rounded-lg p-3 space-y-2">
          <p className="text-sm font-medium text-gray-700">Verified Pro Benefits:</p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span><strong>Priority job access</strong> - Get leads first</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span><strong>Trusted badge</strong> - Stand out to customers</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span><strong>2x more likely</strong> to get hired</span>
            </li>
          </ul>
        </div>

        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-sm text-emerald-800">
            <strong>Quick verification:</strong> Just upload your ID and take a selfie. Takes about 2-3 minutes.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={startVerification}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
            data-testid="start-verification-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Starting...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                Get Verified Now
              </>
            )}
          </Button>
          
          {/* Sandbox approve button for testing */}
          {user?.persona_inquiry_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={sandboxApprove}
              disabled={loading}
              className="text-green-700 border-green-300 hover:bg-green-100"
              data-testid="sandbox-approve-btn"
            >
              Simulate Approval (Sandbox)
            </Button>
          )}
        </div>
        
        <p className="text-xs text-gray-500">
          Powered by Persona — Your data is secure and encrypted.
        </p>
      </CardContent>
    </Card>
  );
}
